/**
 * Canvas Layout Mutations
 *
 * Shared composable providing all canvas layout mutation operations.
 * Used by both `useCanvasLayoutQuery` (single-layout per route) and
 * `useReportsCanvas` (multi-layout per route) to avoid duplicating
 * mutation setup and transform logic.
 *
 * Each mutation function accepts an optional Apollo `update` callback
 * so consumers can manage their own cache strategy.
 */

import { useMutation, type MutateOverrideOptions } from "@vue/apollo-composable";
import {
  CreateCanvasLayoutDocument,
  UpdateCanvasLayoutDocument,
  DeleteCanvasLayoutDocument,
  MarkCanvasLayoutAsDefaultDocument,
  SetUserCanvasLayoutPreferenceDocument,
  ShareCanvasLayoutDocument,
  UnshareCanvasLayoutDocument,
  type CreateCanvasLayoutInput,
  type UpdateCanvasLayoutInput,
} from "~/graphql/generated/graphql";
import type { CanvasConfig } from "~/helpers/canvas/canvas-types";
import { transformConfigToInput } from "~/composables/useCanvasLayoutTransform";

type MutateOptions<TResult> = MutateOverrideOptions<TResult>;

export function useCanvasLayoutMutations() {
  const createMutation = useMutation(CreateCanvasLayoutDocument);
  const updateMutation = useMutation(UpdateCanvasLayoutDocument);
  const deleteMutation = useMutation(DeleteCanvasLayoutDocument);
  const markDefaultMutation = useMutation(MarkCanvasLayoutAsDefaultDocument);
  const setPreferenceMutation = useMutation(SetUserCanvasLayoutPreferenceDocument);
  const shareMutation = useMutation(ShareCanvasLayoutDocument);
  const unshareMutation = useMutation(UnshareCanvasLayoutDocument);

  const saving = computed(() => createMutation.loading.value || updateMutation.loading.value);
  const deleting = computed(() => deleteMutation.loading.value);
  const mutating = computed(
    () =>
      createMutation.loading.value ||
      updateMutation.loading.value ||
      deleteMutation.loading.value ||
      markDefaultMutation.loading.value ||
      setPreferenceMutation.loading.value ||
      shareMutation.loading.value ||
      unshareMutation.loading.value,
  );

  /** Create a new canvas layout */
  async function createLayout(input: CreateCanvasLayoutInput, options?: MutateOptions<any>) {
    return createMutation.mutate({ input }, options);
  }

  /** Update an existing canvas layout's config */
  async function updateLayout(id: string, config: CanvasConfig, options?: MutateOptions<any>) {
    const transformed = transformConfigToInput(config);
    const input: UpdateCanvasLayoutInput = {
      name: config.name ?? "",
      ...transformed,
    };
    return updateMutation.mutate({ id, input }, options);
  }

  /** Delete a canvas layout by id */
  async function deleteLayout(id: string, options?: MutateOptions<any>) {
    return deleteMutation.mutate({ id }, options);
  }

  /** Mark a canvas layout as the org-wide default */
  async function markAsDefault(id: string, options?: MutateOptions<any>) {
    return markDefaultMutation.mutate({ id }, options);
  }

  /** Set a canvas layout as the current user's preference */
  async function setUserPreference(id: string, options?: MutateOptions<any>) {
    return setPreferenceMutation.mutate({ id }, options);
  }

  /** Share a canvas layout */
  async function shareLayout(id: string, options?: MutateOptions<any>) {
    return shareMutation.mutate({ id }, options);
  }

  /** Unshare a canvas layout */
  async function unshareLayout(id: string, options?: MutateOptions<any>) {
    return unshareMutation.mutate({ id }, options);
  }

  return {
    createLayout,
    updateLayout,
    deleteLayout,
    markAsDefault,
    setUserPreference,
    shareLayout,
    unshareLayout,
    saving,
    deleting,
    mutating,
  };
}
